// src/pages/portal/Page_therapist/Page_Therapist_Salas/useSalasController.js
import { useMemo } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks";
import { MozillaHubRoomsApi } from "../../../../api";

import {
  useTherapistIdentity,
  useSalasCatalog,
  useMyTherapies,
  useSceneModal,
  useTherapyWizardState,
  useTherapyDetail,
  useSharedTherapies,
  useAddSceneWizardBridge,
} from "./controller";

const mozillaHubRoomsApi = new MozillaHubRoomsApi();

// Origen del Hub GCP (mismo que el backend y el resto de frontends).
const HUB_ORIGIN = "https://educa360.es";

// Extrae el scene_id de una URL de escena (.../scenes/<id>). null si no es escena Hubs
// (enlace externo o visor 360) -> en ese caso se abre la URL tal cual.
function extractSceneId(url) {
  const m = String(url || "").match(/\/scenes\/([^/?#]+)/);
  return m ? m[1] : null;
}

export function useSalasController() {
  // Los avisos de este controlador se leen en pantalla, asi que tambien salen
  // del fichero de idioma aunque no sean JSX.
  const { t } = useTranslation("therapist");
  const { accessToken, user } = useAuth();
  const { therapistId } = useTherapistIdentity(user);

  // Catálogo (categorías + escenas)
  const catalog = useSalasCatalog({ accessToken });

  // Sidebar (mis terapias)
  const my = useMyTherapies({ accessToken, therapistId, limit: 10 });

  // Modal detalle de escena (info)
  const sceneModal = useSceneModal();

  // Detalle de terapia (sidebar + modal)
  const detail = useTherapyDetail({
    accessToken,
    onAfterChange: my.refreshTherapies,
  });

  // Wizard crear/editar terapia
  const wizard = useTherapyWizardState({
    onAfterSuccess: my.refreshTherapies,
  });

  // Marketplace compartidas
  const shared = useSharedTherapies({
    accessToken,
    onAfterClone: my.refreshTherapies,
  });

  // Puente para el nuevo wizard "Añadir escena a terapia"
  const addScene = useAddSceneWizardBridge({
    onSuccess: (therapy) => {
      my.refreshTherapies();
      if (therapy?._id) {
        detail.handleViewTherapy({ _id: therapy._id });
      }
    },
  });

  // Accion: lanzar sala. SEC-HUB-02 (REVI): NO abrir la URL cruda de la escena
  // (permanente, robable). Genera una sala efimera con caducidad 3h (LoadRoom ->
  // createRoom marca userData; el actuador la cierra al vencer) y la abre con
  // embed_token. Enlaces externos/visores 360 (sin /scenes/) se abren tal cual.
  const launchScene = async (scene) => {
    const url = scene?.module_01?.ModOneUrlsala;
    if (!url) {
      message.error(t("salas.avisos.urlNoDisponible"));
      return;
    }

    const sceneId = extractSceneId(url);
    if (!sceneId) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    // Abrimos la pestana YA (dentro del gesto de click) para evitar el bloqueo de
    // popups; la rellenamos cuando el backend cree la sala. Cortamos opener.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;

    try {
      // `source` clasifica la apertura en la metrica del dashboard admin. Las
      // instituciones REVI llevan role:"institution" igual que las K12, asi que
      // este flujo es lo unico que evita que la apertura cuente como K12.
      const result = await mozillaHubRoomsApi.LoadRoom(accessToken, sceneId, {
        source: "revi_session",
        sceneRefId: scene?._id,
      });
      const roomUrl = `${HUB_ORIGIN}/${result.room.id}?embed_token=${result.room.embedToken}`;
      if (tab) tab.location = roomUrl;
      else window.open(roomUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al abrir la sala de la escena (REVI):", error);
      if (tab) tab.close();
      message.error(t("salas.avisos.errorAbrirSala"));
    }
  };

  // Loading total
  const loading = useMemo(() => {
    return catalog.loading || my.myTherapiesLoading;
  }, [catalog.loading, my.myTherapiesLoading]);

  return {
    // Data
    loading,
    categories: catalog.categories,
    scenes: catalog.scenes,
    myTherapies: my.myTherapies,
    cargarMasTerapias: my.cargarMasTerapias,
    cargandoMasTerapias: my.cargandoMasTerapias,
    totalTerapias: my.totalTerapias,
    quedanTerapias: my.quedanTerapias,
    selectedCat: catalog.selectedCat,
    setSelectedCat: catalog.setSelectedCat,

    // Grid Modal
    modalState: sceneModal.modalState,
    openModal: sceneModal.openModal,
    closeModal: sceneModal.closeModal,
    launchScene,

    // Wizard terapia
    wizardState: wizard.wizardState,
    openWizard: wizard.openWizard,
    closeWizard: wizard.closeWizard,
    handleTherapyCreated: wizard.handleTherapyCreated,
    handleEditTherapy: wizard.handleEditTherapy,

    // Detalle terapia
    currentTherapyDetail: detail.currentTherapyDetail,
    isDetailModalOpen: detail.isDetailModalOpen,
    detailLoading: detail.detailLoading,
    handleViewTherapy: detail.handleViewTherapy,
    closeDetailModal: detail.closeDetailModal,
    handleRemoveStep: detail.handleRemoveStep,

    // ✅ IMPORTANTE: exporta el nuevo handler para el botón ↑
    handleMoveStepUp: detail.handleMoveStepUp,

    handleDeleteTherapy: detail.handleDeleteTherapy,

    // Marketplace
    sharedOpen: shared.sharedOpen,
    sharedLoading: shared.sharedLoading,
    sharedTherapies: shared.sharedTherapies,
    sharedPage: shared.sharedPage,
    sharedPageSize: shared.sharedPageSize,
    sharedTotal: shared.sharedTotal,
    openSharedModal: shared.openSharedModal,
    closeSharedModal: shared.closeSharedModal,
    handleSharedSearch: shared.handleSharedSearch,
    handleSharedPageChange: shared.handleSharedPageChange,
    handleCloneSharedTherapy: shared.handleCloneSharedTherapy,

    // AddScene wizard
    addSceneWizard: addScene.addSceneWizard,
    openAddSceneWizard: addScene.openAddSceneWizard,
    closeAddSceneWizard: addScene.closeAddSceneWizard,
    handleAddSceneSuccess: addScene.handleAddSceneSuccess,
  };
}
